import {
  Badge,
  Box,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { getIn, useFormikContext } from "formik";
import { useState } from "react";
import type { SyntheticEvent } from "react";
import type { AddSolutionFormValues } from "../../../add/form/types";
import type { EditSolutionFormValues } from "../../../edit/form/types";
import NumberField from "../scores/NumberField";
import IntegrityScoreTable from "./IntegrityScoreTable";
import { assessmentScorePillars } from "./scoreCategories";
import type { AssessmentScoreTableRow } from "./types";
import { AssessmentScorePillar } from "../../../types";

function IntegrityScores() {
  const context = useFormikContext<
    AddSolutionFormValues | EditSolutionFormValues
  >();
  const [selectedPillar, setSelectedPillar] =
    useState<AssessmentScorePillar>("carbon");
  const pillar =
    assessmentScorePillars.find(({ key }) => key === selectedPillar) ??
    assessmentScorePillars[0];

  const rows: AssessmentScoreTableRow[] = pillar.categories.map(
    ({ key, label }) => {
      const basePath = `pillars.${pillar.key}.scores.${key}`;
      const scorePath = `${basePath}.score`;
      const explanationPath = `${basePath}.explanation`;
      const statusPath = `${basePath}.status`;
      const scoreError = getIn(context.errors, scorePath) as string | undefined;
      const explanationError = getIn(context.errors, explanationPath) as
        string | undefined;
      const statusError = getIn(context.errors, statusPath) as
        string | undefined;
      return {
        key,
        label,
        scoreInvalid: Boolean(scoreError),
        evidenceInvalid: Boolean(explanationError),
        score: (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <NumberField
              id={`${pillar.key}-${key}-score`}
              label="Score"
              min={0}
              max={10}
              step={0.1}
              smallStep={0.1}
              snapOnStep
              value={
                (getIn(context.values, scorePath) as number | undefined) ?? null
              }
              onValueChange={(value: number | null) =>
                context.setFieldValue(scorePath, value ?? undefined)
              }
              onValueCommitted={() => context.setFieldTouched(scorePath, true)}
              error={Boolean(scoreError)}
              helperText={scoreError}
            />
            <TextField
              select
              name={statusPath}
              label="Status"
              value={
                (getIn(context.values, statusPath) as
                  string | null | undefined) ?? ""
              }
              onChange={context.handleChange}
              onBlur={context.handleBlur}
              error={Boolean(statusError)}
              helperText={statusError}
              size="small"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="highest">Highest</MenuItem>
              <MenuItem value="lowest">Lowest</MenuItem>
            </TextField>
          </Box>
        ),
        evidence: (
          <TextField
            name={explanationPath}
            label="Evidence"
            value={
              (getIn(context.values, explanationPath) as string | undefined) ??
              ""
            }
            onChange={context.handleChange}
            onBlur={context.handleBlur}
            error={Boolean(explanationError)}
            helperText={explanationError}
            multiline
            minRows={4}
            fullWidth
          />
        ),
      };
    },
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Tabs
        value={pillar.key}
        onChange={(_event: SyntheticEvent, nextPillar: AssessmentScorePillar) =>
          setSelectedPillar(nextPillar)
        }
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Integrity edit pillar tabs"
        sx={{ mb: 2 }}
      >
        {assessmentScorePillars.map(({ key, label, categories }) => {
          const hasErrors = categories.some(({ key: categoryKey }) => {
            const basePath = `pillars.${key}.scores.${categoryKey}`;
            return Boolean(
              getIn(context.errors, `${basePath}.score`) ||
              getIn(context.errors, `${basePath}.explanation`),
            );
          });

          return (
            <Tab
              key={key}
              value={key}
              data-missing={hasErrors ? "true" : "false"}
              label={
                <Badge
                  color="error"
                  variant="dot"
                  invisible={!hasErrors}
                  sx={{ "& .MuiBadge-badge": { right: -8, top: 4 } }}
                >
                  {label}
                </Badge>
              }
            />
          );
        })}
      </Tabs>

      <Typography
        component="div"
        role="tabpanel"
        aria-label={`${pillar.label} integrity inputs`}
      >
        <IntegrityScoreTable heading={pillar.label} rows={rows} />
      </Typography>
    </Box>
  );
}

export default IntegrityScores;
